/**
 * applyTenderFilters — shared client-side filter pipeline.
 *
 * Used by surfaces that filter a pre-loaded array of tenders/bookmarks
 * (My Tenders List, My Tenders Team, etc.). The chip row component
 * produces this filter shape; this helper consumes it.
 *
 * Tolerates field aliases (snake_case + camelCase) since BookmarkContext,
 * the team feed, and direct ES queries each normalize differently.
 *
 * Filter shape:
 *   {
 *     status:       'all' | 'open' | 'closing_soon' | 'closed',
 *     categories:   string[],
 *     fromDate:     ISO date | null,   // publish-date lower bound
 *     toDate:       ISO date | null,   // publish-date upper bound
 *     closingFrom:  ISO date | null,
 *     closingTo:    ISO date | null,
 *     member:       string | 'all',    // user_name (team view only)
 *   }
 */

const getStatus = (t) => (t.status || '').toLowerCase();
const getClosing = (t) => t.closing_date || t.closingDate || t['tenderClosingDate-appelOffresDateCloture'] || null;
const getPublish = (t) => t.publish_date || t.publishDate || t['tenderPublishedDate-appelOffresDatePublication'] || null;
const getCategories = (t) => {
  if (Array.isArray(t.categories) && t.categories.length) return t.categories;
  if (t.procurementCategory) return [t.procurementCategory];
  if (t['masterCategory-catégoriePrincipale']) return [t['masterCategory-catégoriePrincipale']];
  return [];
};
const getMember = (t) => t.user_name || t.userName || t.user_id || null;

const isClosed = (closing) => {
  if (!closing) return false;
  try {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const d = new Date(closing); d.setHours(0, 0, 0, 0);
    return d < today;
  } catch { return false; }
};
const isClosingSoon = (closing) => {
  if (!closing) return false;
  try {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const d = new Date(closing); d.setHours(0, 0, 0, 0);
    const diffDays = (d - today) / (1000 * 60 * 60 * 24);
    return diffDays >= 0 && diffDays <= 14;
  } catch { return false; }
};

const inRange = (iso, lo, hi) => {
  if (!iso) return !(lo || hi); // missing date passes only if no range constraint
  const ts = new Date(iso).getTime();
  if (lo && ts < new Date(lo).getTime()) return false;
  if (hi && ts > new Date(hi).getTime() + 86400_000 - 1) return false; // inclusive end-of-day
  return true;
};

// True if the tender is closed — by status text or a past closing date.
export const isTenderClosed = (t) => {
  const s = (t.status || '').toLowerCase();
  if (s.includes('closed') || s.includes('expired') || s.includes('passed') ||
      s.includes('awarded') || s.includes('cancelled')) return true;
  return isClosed(getClosing(t));
};

export const FILTER_DEFAULTS = Object.freeze({
  status: 'all',
  categories: [],
  fromDate: null,
  toDate: null,
  closingFrom: null,
  closingTo: null,
  member: 'all',
});

export const hasActiveFilters = (f) => {
  if (!f) return false;
  if (f.status && f.status !== 'all') return true;
  if (f.categories?.length) return true;
  if (f.fromDate || f.toDate) return true;
  if (f.closingFrom || f.closingTo) return true;
  if (f.member && f.member !== 'all') return true;
  return false;
};

export const applyTenderFilters = (items, filters) => {
  if (!filters || !hasActiveFilters(filters)) return items;

  return items.filter((t) => {
    // Status
    if (filters.status && filters.status !== 'all') {
      const s = getStatus(t);
      const closing = getClosing(t);
      if (filters.status === 'open') {
        if (isClosed(closing)) return false;
        if (s && (s.includes('closed') || s.includes('expired') || s.includes('passed'))) return false;
      } else if (filters.status === 'closing_soon') {
        if (!isClosingSoon(closing)) return false;
      } else if (filters.status === 'closed') {
        if (!isClosed(closing) && !s.match(/closed|expired|passed/)) return false;
      }
    }

    // Categories — match any of the selected
    if (filters.categories?.length) {
      const cats = getCategories(t);
      if (!filters.categories.some((c) => cats.includes(c))) return false;
    }

    // Publish date range
    if (filters.fromDate || filters.toDate) {
      if (!inRange(getPublish(t), filters.fromDate, filters.toDate)) return false;
    }

    // Closing date range
    if (filters.closingFrom || filters.closingTo) {
      if (!inRange(getClosing(t), filters.closingFrom, filters.closingTo)) return false;
    }

    // Member (team view)
    if (filters.member && filters.member !== 'all') {
      if (getMember(t) !== filters.member) return false;
    }

    return true;
  });
};
